/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Shared base component for LogicFlow full and side views — extends ObjectBaseModeComponent. Manages flow steps loading, versioning actions (publish, new version, export), and status display.
 */
import {Component, EventEmitter, OnInit, Output, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {ConfirmationService, MenuItem} from "primeng/api";
import {Subscription} from "rxjs";
import {ObjectBaseModeComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base-mode.component";
import {MvsCoreService} from "../../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../../core/shared/service/crud/mvs-crud.service";
import {MvsMessageService} from "../../../../../../core/shared/service/message/mvs-message.service";
import {ObserverService} from "../../../../../../core/shared/object/service/observer.service";
import {LogicFlowService} from "../../../../service/api/logic-flow.service";
import {LogicFlowDto} from "../../../../model/dto/entity/logic-flow.dto";
import {LogicFlowStepDto} from "../../../../model/dto/entity/logic-flow-step.dto";
import {LogicFlowStatusEnum} from "../../../../enum/logic-flow-status.enum";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../../core/shared/misc/sorting";
import {ObjectRequestList} from "../../../../../../core/shared/dto/object/request/object-request-list";
import {DtoList} from "../../../../../../core/shared/dto/dto.list";
import {QlService} from "../../../../../../core/shared/ql/service/ql.service";
import {
    LogicFlowInheritanceResolverService,
    ResolvedFlow
} from "../../../../service/inheritance/logic-flow-inheritance-resolver.service";
import {LogicFlowImportDto} from "../../../../model/dto/entity/logic-flow-import.dto";
import {LogicFlowExportDto} from "../../../../model/dto/entity/logic-flow-export.dto";

@Component({
    selector: 'mvs-lg-logic-flow-object-component-base',
    template: '',
    standalone: false
})
export class LgLogicFlowObjectBaseComponent extends ObjectBaseModeComponent implements OnInit {

    dto: LogicFlowDto;
    flowSteps: LogicFlowStepDto[] = [];
    resolvedFlow?: ResolvedFlow;
    flowStatus: number = LogicFlowStatusEnum.DRAFT;
    flowVersion: number = 1;
    isPublished: boolean = false;
    publishing: boolean = false;
    creatingVersion: boolean = false;

    /** Expose enum to template */
    LogicFlowStatusEnum = LogicFlowStatusEnum;

    /**
     * @ticket TM-1000001563
     * Declared import definitions for the current flow, loaded from lg.LogicFlowImport.
     * Passed into <lg-flow-designer> so Start/End cards can display the declared schema.
     */
    // TM-1000001563: holds loaded LogicFlowImport entities for the current flow
    flowImportsList: LogicFlowImportDto[] = [];

    /**
     * @ticket TM-1000001563
     * Declared export definitions for the current flow, loaded from lg.LogicFlowExport.
     * Passed into <lg-flow-designer> so Start/End cards can display the declared schema.
     */
    // TM-1000001563: holds loaded LogicFlowExport entities for the current flow
    flowExportsList: LogicFlowExportDto[] = [];

    // TM-1000001563: services resolved via MvsCoreService for loading imports/exports
    protected logicFlowImportService: MvsCrudService;
    protected logicFlowExportService: MvsCrudService;

    private flowStepSubscription: Subscription;
    private publishSubscription: Subscription;
    private createVersionSubscription: Subscription;
    private exportSubscription: Subscription;
    // TM-1000001563: subscriptions for loading declared import/export lists
    private flowImportsSubscription: Subscription;
    private flowExportsSubscription: Subscription;

    @Output() onBreadcrumbItems: EventEmitter<MenuItem[]> = new EventEmitter();

    constructor(
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService,
        protected confirmationService: ConfirmationService,
        protected messageService: MvsMessageService,
        protected observerService: ObserverService,
        protected logicFlowService: LogicFlowService,
        protected router: Router,
        protected qlService: QlService,
        protected inheritanceResolver: LogicFlowInheritanceResolverService
    ) {
        super(route);
    }

    ngOnInit() {
        // initialize navigation items
        this.loadSideBarMenuItems();

        super.ngOnInit();

        // TM-1000001563: obtain services for loading declared import/export definitions
        this.logicFlowImportService = this.coreService.getCrudService('lg.LogicFlowImport');
        this.logicFlowExportService = this.coreService.getCrudService('lg.LogicFlowExport');

        if (this.dto) {
            this.onObjectChanged();
        } else {
            this.loadData();
        }

        this.initialized = true;
    }

    loadData() {
        this.logicFlowService.get(this.objectIdentifier.objectId).subscribe((res: LogicFlowDto) => {
            this.dto = res;
            this.onObjectChanged();
        });
    }

    /**
     * load and initialize sidebar navigations
     */
    loadSideBarMenuItems() {
    }

    onObjectChanged() {
        this.flowStatus = this.dto.flowStatus ?? LogicFlowStatusEnum.DRAFT;
        this.flowVersion = this.dto.version ?? 1;
        this.isPublished = this.flowStatus === LogicFlowStatusEnum.PUBLISHED;
        this.loadFlowSteps();
        // TM-1000001563: reload declared import/export lists whenever flow data changes
        this.loadFlowImportsExports();

        const item: MenuItem[] = [{
            icon: 'fa-regular fa-diagram-project',
            label: this.dto.name,
            id: this.dto.id as unknown as string,
        }];

        this.onBreadcrumbItems.emit(item);
    }

    /**
     * @ticket TM-1000001563
     * Load both LogicFlowImport and LogicFlowExport lists for the current flow in parallel.
     * Results are stored in flowImportsList / flowExportsList and passed into
     * <lg-flow-designer> so its Start/End cards can display the declared schema.
     * Called after flow data changes so lists stay in sync.
     */
    // TM-1000001563: loads declared import/export variable definitions for the current flow
    loadFlowImportsExports(): void {
        if (!this.objectIdentifier || !this.logicFlowImportService || !this.logicFlowExportService) {
            return;
        }

        const flowId = this.objectIdentifier.objectId;

        // TM-1000001563: cancel any in-flight imports request before issuing new one
        this.flowImportsSubscription?.unsubscribe();
        this.flowExportsSubscription?.unsubscribe();

        const importsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('logicFlow', FilterCriteria.cOperatorEqual, flowId)],
            [new Sorting('variableName', true)]
        );

        // TM-1000001563: load LogicFlowImport list filtered by the current flow
        this.flowImportsSubscription = this.logicFlowImportService.list(importsRequest).subscribe({
            next: (result: DtoList<LogicFlowImportDto>) => {
                this.flowImportsList = (result?.entries ?? []) as LogicFlowImportDto[];
            },
            error: () => {
                this.flowImportsList = [];
            }
        });

        const exportsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('logicFlow', FilterCriteria.cOperatorEqual, flowId)],
            [new Sorting('variableName', true)]
        );

        // TM-1000001563: load LogicFlowExport list filtered by the current flow
        this.flowExportsSubscription = this.logicFlowExportService.list(exportsRequest).subscribe({
            next: (result: DtoList<LogicFlowExportDto>) => {
                this.flowExportsList = (result?.entries ?? []) as LogicFlowExportDto[];
            },
            error: () => {
                this.flowExportsList = [];
            }
        });
    }

    loadFlowSteps() {
        this.flowStepSubscription?.unsubscribe();

        const flowId = this.objectIdentifier.objectId;
        this.flowStepSubscription = this.inheritanceResolver.resolve(flowId).subscribe({
            next: (resolved) => {
                this.resolvedFlow = resolved;
                this.flowSteps = resolved.steps.map(s => s.step);
            },
            error: (err) => {
                console.error('[LgLogicFlowObjectBase] Failed to resolve flow inheritance:', err);
            }
        });
    }

    // ── Versioning computed properties ──

    /**
     * @ticket TM-1000001563
     * Number of steps within the resolved flow that have useMockOutput=true.
     * Used to surface a warning in the publish confirmation dialog when mocks are active.
     */
    // TM-1000001563: mockCount getter added for parity with LgFlowObjectPagePage
    get mockCount(): number {
        return (this.flowSteps ?? []).filter(s => s?.useMockOutput === true).length;
    }

    get flowStatusLabel(): string {
        switch (this.flowStatus) {
            case LogicFlowStatusEnum.PUBLISHED:
                return 'Veröffentlicht';
            case LogicFlowStatusEnum.DEPRECATED:
                return 'Veraltet';
            default:
                return 'Entwurf';
        }
    }

    get flowStatusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        switch (this.flowStatus) {
            case LogicFlowStatusEnum.PUBLISHED:
                return 'success';
            case LogicFlowStatusEnum.DEPRECATED:
                return 'warn';
            default:
                return 'secondary';
        }
    }

    get flowStatusIcon(): string {
        switch (this.flowStatus) {
            case LogicFlowStatusEnum.PUBLISHED:
                return 'fa-regular fa-circle-check';
            case LogicFlowStatusEnum.DEPRECATED:
                return 'fa-regular fa-inbox';
            default:
                return 'fa-regular fa-pencil';
        }
    }

    // ── Versioning actions ──

    /**
     * @ticket TM-1000001563 (Item 3)
     * Publish the current DRAFT flow.
     * Always opens a ConfirmDialog before issuing the backend call — the
     * action is irreversible (published flows can no longer be edited).
     */
    publishFlow(): void {
        if (!this.objectIdentifier || this.publishing) return;

        // TM-1000001563: append mock warning to confirmation message when mocks are active
        const mockWarning = this.mockCount > 0
            ? ` Achtung: Es sind noch ${this.mockCount} Mock-Schritt(e) aktiv.`
            : '';

        // TM-1000001563 (Item 3): always confirm publish; include mock warning when relevant.
        this.confirmationService.confirm({
            header: 'Veröffentlichen?',
            message: 'Soll dieser Flow jetzt veröffentlicht werden? '
                + 'Nach der Veröffentlichung kann der Flow nicht mehr bearbeitet werden.'
                + mockWarning,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Veröffentlichen',
            rejectLabel: 'Abbrechen',
            accept: () => this.doPublish(),
        });
    }

    /**
     * @ticket TM-1000001563 (Item 3)
     * Execute the actual publish call against the backend.
     */
    private doPublish(): void {
        if (!this.objectIdentifier || this.publishing) return;

        this.publishing = true;
        this.publishSubscription?.unsubscribe();

        this.publishSubscription = this.logicFlowService.publishFlow(this.objectIdentifier.objectId).subscribe({
            next: () => {
                this.publishing = false;
                this.loadData();
                this.messageService.showSuccessMessage('Veröffentlicht', 'Der Flow wurde erfolgreich veröffentlicht.');
            },
            error: () => {
                this.publishing = false;
            }
        });
    }

    /**
     * @ticket TM-1000001563 (Item 3)
     * Create a new DRAFT version from the current PUBLISHED flow.
     * Wrapped in a ConfirmDialog so users cannot accidentally fork the
     * published flow.
     */
    createNewVersion(): void {
        if (!this.objectIdentifier || this.creatingVersion) return;

        // TM-1000001563 (Item 3): always confirm new-version creation.
        this.confirmationService.confirm({
            header: 'Neue Version anlegen?',
            message: 'Aus dem aktuell veröffentlichten Flow wird eine neue Draft-Version erzeugt. '
                + 'Sie werden anschließend zur neuen Version weitergeleitet.',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Neue Version',
            rejectLabel: 'Abbrechen',
            accept: () => this.doCreateNewVersion(),
        });
    }

    /**
     * @ticket TM-1000001563 (Item 3)
     * Execute the actual createNewVersion call against the backend.
     */
    private doCreateNewVersion(): void {
        if (!this.objectIdentifier || this.creatingVersion) return;

        this.creatingVersion = true;
        this.createVersionSubscription?.unsubscribe();

        this.createVersionSubscription = this.logicFlowService.createNewVersion(this.objectIdentifier.objectId).subscribe({
            next: (result: any) => {
                this.creatingVersion = false;
                if (result?.flowId) {
                    this.router.navigate(['/lg/flow', result.flowId]);
                }
            },
            error: () => {
                this.creatingVersion = false;
            }
        });
    }

    exportFlow(): void {
        if (!this.objectIdentifier) return;

        this.exportSubscription?.unsubscribe();

        this.exportSubscription = this.logicFlowService.exportFlow(this.objectIdentifier.objectId, true, true).subscribe((blob: Blob) => {
            const objectUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = `logic-flow-export-${this.objectIdentifier.objectId}.json`;
            anchor.click();
            window.URL.revokeObjectURL(objectUrl);
        });
    }

    onFlowStepSelected(step: LogicFlowStepDto) {
        // Override in subclasses if needed
    }

    onFlowStepEdit(step: LogicFlowStepDto) {
        // Override in subclasses if needed
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }
    }

    ngOnDestroy() {
        this.flowStepSubscription?.unsubscribe();
        this.publishSubscription?.unsubscribe();
        this.createVersionSubscription?.unsubscribe();
        this.exportSubscription?.unsubscribe();
        // TM-1000001563: clean up import/export subscriptions
        this.flowImportsSubscription?.unsubscribe();
        this.flowExportsSubscription?.unsubscribe();
    }

    handleNavigationItemsChange(): void {
        this.onNavigationItems.emit(this.navigationItems);
    }

}
